import {
  WSActions,
  WSControl,
  WSDivider,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSForm,
  WSList,
  WSLoader,
  WSModalOld,
  WSSelect,
  WSSelectOption,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayoutPreferences } from "@wingspanhq/payments/dist/interfaces";
import {
  AccountExpand,
  PayoutDestinationType,
  PayoutSettingsStandardDestination,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { WSQueryCache } from "@ws-react-query";
import React, { useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as Yup from "yup";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import {
  queriesAccounts,
  queriesInvoiceRows,
  queriesExternalBankAccounts,
  queriesInternalAccounts,
  queriesPaymentCards,
  queriesPayouts
} from "@query";
import {
  getAccountLabelProps,
  getInternalAccountLabelProps,
  getPaymentCardLabelProps
} from "../../modules/Settings/components/ManageMethods/utils";
import { PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD } from "../../modules/Settings/paths";
import { payoutsApi } from "@api";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { useSession } from "@hooks/useSession";
import { selectorExternalBankAccountIPReady } from "@selectors/selectorExternalBankAccountIPReady";
import { selectorPayoutSettingsInstantFee } from "@selectors/selectorPayoutSettingsInstantFee";
import { useWSMutation } from "../../query/helpers";
import { QUERY_INVOICE, QUERY_INVOICES } from "../../query/payments/keys";
import { useQueryInvoice } from "../../query/payments/queries";
import { paymentsService } from "../../services/payments";
import { ErrorContextKey } from "../../services/platform";
import { selectorActiveBankingAccount } from "../../shared/selectors/selectorActiveBankingAccount";
import { isCountryUS } from "../../shared/selectors/selectorIsDomesticMember";
import { selectorPaymentCardPayoutReady } from "../../shared/selectors/selectorPaymentCardPayoutReady";
import { instantPayoutService } from "../../shared/services/instantPayout";
import { track } from "../../utils/analytics";
import { WSServiceError } from "../../utils/serviceHelper";

function destinationToStringValue(
  destination?: PayoutSettingsStandardDestination | null
) {
  switch (destination?.type) {
    case PayoutDestinationType.EXTERNAL_BANK_ACCOUNT:
      return `account ${destination?.id}`;

    case PayoutDestinationType.PAYMENT_CARD:
      return `payment-card ${destination?.id}`;

    default:
      return null;
  }
}

type FormData = {
  payoutMethod: null | string;
  enableInstantDeposits: boolean;
};

export const InstantPayoutsModal: React.FC<
  RouteComponentProps<{
    invoiceId: string;
  }>
> = ({ match, history, location }) => {
  const onBack = () => {
    history.goBack();
  };
  const { accountId } = useSession();

  const invoiceId = match.params.invoiceId;
  const queryInvoice = useQueryInvoice(invoiceId);
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const queryPaymentCards = queriesPaymentCards.listPaymentCards.useQuery();
  const queryAccount = queriesAccounts.getAccount.useQuery(
    { accountId, expand: [AccountExpand.COMPLIANCE_ENTITY] },
    { enabled: !!accountId }
  );
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  const [successPayoutMethodLabel, setSuccessPayoutMethodLabel] = useState<
    string | null
  >(null);

  const isInternational = queryAccount.data
    ? !isCountryUS(queryAccount.data.complianceEntity?.address?.country)
    : false;

  const payoutMethodOptions = useMemo(() => {
    const options: WSSelectOption[] = [];

    const externalBankAccounts = (
      queryExternalBankAccounts.data?.data || []
    ).filter(externalBankAccount =>
      selectorExternalBankAccountIPReady(externalBankAccount, isInternational)
    );
    const paymentCards = (queryPaymentCards.data?.data || []).filter(
      selectorPaymentCardPayoutReady
    );

    const internalAccounts = (queryInternalAccounts.data?.data || []).filter(
      selectorActiveBankingAccount
    );

    internalAccounts.forEach(internalAccount => {
      const label = getInternalAccountLabelProps(internalAccount);
      options.push({
        avatar: label.avatar,
        label: [label.link?.label || label.text, label.secondaryText]
          .filter(Boolean)
          .join(" "),
        value: "internal-account " + internalAccount.id
      });
    });

    externalBankAccounts.forEach(externalBankAccount => {
      const label = getAccountLabelProps(externalBankAccount);
      options.push({
        avatar: label.avatar,
        label: [label.text, label.secondaryText].filter(Boolean).join(" "),
        value: "account " + externalBankAccount.id
      });
    });

    paymentCards.forEach(paymentCard => {
      const label = getPaymentCardLabelProps(paymentCard);

      options.push({
        avatar: label.avatar,
        label: [label.text, label.secondaryText].filter(Boolean).join(" "),
        value: "payment-card " + paymentCard.id
      });
    });

    return options;
  }, [
    queryExternalBankAccounts.data?.data,
    queryPaymentCards.data,
    queryInternalAccounts.data,
    isInternational
  ]);

  const [submit, submitMeta] = useWSMutation<void, WSServiceError, FormData>(
    async data => {
      if (!data.payoutMethod) {
        throw new Error("No payout method set");
      }

      track("Invoice Instant Deposit Requested", {
        invoiceId: invoiceId
      });

      const [payoutMethodType, destinationId] = data.payoutMethod.split(" ");

      let instantDestination:
        | PayoutSettingsStandardDestination
        | null
        | undefined;

      switch (payoutMethodType) {
        case "account":
          instantDestination = {
            type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT,
            id: destinationId
          };
          break;
        case "payment-card":
          instantDestination = {
            type: PayoutDestinationType.PAYMENT_CARD,
            id: destinationId
          };
          break;

        case "internal-account":
          instantDestination = null;
          break;
      }

      if (instantDestination === undefined) {
        throw new Error("Invalid payout method");
      }

      const payoutSettingsId = queryPayoutSettings.data?.id;
      if (payoutSettingsId) {
        await payoutsApi.updatePayoutSettings({
          payoutSettingsId,
          payoutSettingsUpdate: {
            preferredPayoutSpeed: data.enableInstantDeposits
              ? PreferredPayoutSpeed.INSTANT
              : undefined,
            instantDestination
          }
        });
      }

      await paymentsService.invoice.update(invoiceId, {
        member: {
          payoutPreferences: PayoutPreferences.Instant
        }
      });
    },
    {
      onSuccess: (_data, variables) => {
        const selectedOption = payoutMethodOptions.find(
          o => o.value === variables.payoutMethod
        );
        setSuccessPayoutMethodLabel(selectedOption?.label || "");

        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
        WSQueryCache.invalidateQueries(
          queriesPayouts.listPayoutSettings.queryKeyBase
        );
      }
    }
  );

  const invoice = queryInvoice.data;
  const payoutSettings = queryPayoutSettings.data;

  const invoiceIsDeposited = !!invoice?.events.depositedAt;

  const invoiceAmount = Math.abs(invoice?.amountDetails?.memberGross || 0);
  const instantPayoutFee =
    instantPayoutService.calculateInvoiceInstantPayoutFee(
      Math.abs(invoiceAmount),
      payoutSettings
    );
  const instantPayoutAmount = invoiceAmount - instantPayoutFee;

  if (
    !queryInvoice.data ||
    !queryPayoutSettings.data ||
    !queryExternalBankAccounts.data ||
    !queryPaymentCards.data ||
    !queryInternalAccounts.data
  ) {
    return (
      <WSModalOld size="S" onClose={onBack}>
        <WSLoader.Spinner />
      </WSModalOld>
    );
  }

  if (successPayoutMethodLabel !== null) {
    return (
      <WSModalOld
        size="S"
        title={`Instant payout for invoice #${invoice?.invoiceNumber}`}
        onClose={onBack}
      >
        <WSList gap="XL">
          <WSEmptyState
            type="done"
            title="Instant payout requested"
            description={`Your instant payout request for invoice #${invoice?.invoiceNumber} to ${successPayoutMethodLabel} will be processed within 90 minutes.`}
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Done",
                onClick: onBack
              }
            ]}
          />
        </WSList>
      </WSModalOld>
    );
  }

  if (invoiceIsDeposited) {
    return (
      <WSModalOld
        size="S"
        title={`Invoice #${queryInvoice.data?.invoiceNumber}`}
        onClose={onBack}
      >
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            This invoice has already been deposited
          </WSText.ParagraphSm>

          <WSActions
            orientation="vertical"
            buttons={[
              {
                kind: "Tertiary",
                label: "Close",
                onClick: onBack
              }
            ]}
          />
        </WSList>
      </WSModalOld>
    );
  }

  if (!payoutMethodOptions.length) {
    return (
      <WSModalOld
        size="S"
        title={`Instant payout for invoice #${
          queryInvoice.data?.invoiceNumber || "###"
        }`}
        onClose={onBack}
      >
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            No instant payout method added
          </WSText.ParagraphSm>

          <WSActions
            orientation="vertical"
            buttons={[
              {
                kind: "Tertiary",
                label: "Close",
                onClick: onBack
              },
              {
                kind: "Secondary",
                label: "Add payout method",
                onClick: () => {
                  history.push(PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD);
                }
              }
            ]}
          />
        </WSList>
      </WSModalOld>
    );
  }

  return (
    <WSModalOld
      size="S"
      title={`Instant payout for invoice #${
        queryInvoice.data?.invoiceNumber || "###"
      }`}
      onClose={onBack}
    >
      <WSList gap="XL">
        <WSText.ParagraphSm color="gray500">
          Instant payout requests are generally processed within 90 minutes.
        </WSText.ParagraphSm>
        <WSElement>
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm weight="medium">
              Invoice number
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>{invoice?.invoiceNumber}</WSText.ParagraphSm>
          </WSFlexBox.CenterY>
          <WSDivider my="M" />
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm weight="medium">
              Invoice amount
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {formatMoney(invoiceAmount, invoice?.currency || "USD")}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
          <WSDivider my="M" />
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm weight="medium">
              Instant payout fee (
              {selectorPayoutSettingsInstantFee(payoutSettings)}%)
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {formatMoney(instantPayoutFee, invoice?.currency || "USD")}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
          <WSDivider my="M" />
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm weight="medium">
              Instant payout amount
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {formatMoney(instantPayoutAmount, invoice?.currency || "USD")}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
          <WSDivider my="M" />
        </WSElement>

        <WSForm<FormData>
          defaultValues={{
            payoutMethod: destinationToStringValue(
              queryPayoutSettings.data?.instantDestination
            ),
            enableInstantDeposits: false
          }}
          onSubmit={submit}
          validationSchema={Yup.object({
            payoutMethod: Yup.string().nullable().required("Required"),
            enableInstantDeposits: Yup.boolean().nullable()
          })}
        >
          <WSList gap="XL">
            <WSList gap="M">
              <WSForm.Field
                name="payoutMethod"
                component={WSSelect}
                label="Instant payout account"
                componentProps={{
                  options: payoutMethodOptions
                }}
              />

              <WSForm.Field
                name="enableInstantDeposits"
                component={WSControl}
                componentProps={{
                  type: "checkbox",
                  size: "S",
                  label:
                    "Make instant payouts my default for all future payments"
                }}
              />
            </WSList>

            <WSErrorMessage
              contextKey={ErrorContextKey.Other}
              error={submitMeta.error}
            />

            <WSActions
              orientation="vertical"
              buttons={[
                {
                  label: "Confirm instant payout request",
                  loading: submitMeta.isLoading
                },
                {
                  label: "Cancel",
                  kind: "Tertiary",
                  type: "button",
                  onClick: onBack
                }
              ]}
            />
          </WSList>
        </WSForm>
      </WSList>
    </WSModalOld>
  );
};
